<template>
  <div class="ka-header">
    <div class="ka-header__row">
      <div class="ka-header__intro">
        <h1 class="ka-header__title">
          Kiosk Analytics
          <span
            v-if="sample"
            class="ka-header__badge"
            title="The kiosk analytics API is not available yet - showing generated sample data"
          >
            Sample data
          </span>
        </h1>
        <p class="ka-header__subtitle">
          Kiosk sessions, bookings &amp; video calls · {{ formattedRange }}
        </p>
      </div>

      <div class="ka-header__controls">
        <div class="ka-header__dates">
          <div class="ka-header__date-field">
            <feather-icon icon="CalendarIcon" svgClasses="ka-header__date-icon" />
            <datepicker
              :value="dateFrom"
              :disabled-dates="disabledDatesFrom"
              format="dd MMM yyyy"
              input-class="ka-header__date-input"
              placeholder="Start date"
              @selected="onStartDateSelected"
            />
          </div>
          <feather-icon icon="ArrowRightIcon" svgClasses="ka-header__date-arrow" />
          <div class="ka-header__date-field">
            <feather-icon icon="CalendarIcon" svgClasses="ka-header__date-icon" />
            <datepicker
              :value="dateTo"
              :disabled-dates="disabledDatesTo"
              format="dd MMM yyyy"
              input-class="ka-header__date-input"
              placeholder="End date"
              @selected="onEndDateSelected"
            />
          </div>
        </div>

        <div class="ka-header__select-wrap">
          <select
            class="ka-header__select"
            :value="companyId"
            @change="$emit('update:companyId', $event.target.value)"
          >
            <option
              v-for="company in companyOptions"
              :key="company.id"
              :value="company.id"
            >
              {{ company.name }}
            </option>
          </select>
          <feather-icon icon="ChevronDownIcon" svgClasses="ka-header__select-icon" />
        </div>

        <button
          type="button"
          class="ka-header__btn ka-header__btn--search"
          :disabled="loading"
          @click="$emit('search')"
        >
          <feather-icon icon="SearchIcon" svgClasses="ka-header__btn-icon" />
          {{ loading ? "Searching..." : "Search" }}
        </button>

        <button
          type="button"
          class="ka-header__btn ka-header__btn--download"
          @click="$emit('download')"
        >
          <feather-icon
            icon="DownloadIcon"
            svgClasses="ka-header__btn-icon ka-header__btn-icon--purple"
          />
          Download
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { mapGetters } from "vuex";

export default {
  name: "KioskAnalyticsHeader",
  components: { Datepicker },
  props: {
    dateFrom: { type: Date, required: true },
    dateTo: { type: Date, required: true },
    companyId: { type: [String, Number], default: "all" },
    loading: { type: Boolean, default: false },
    sample: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters("company", {
      storeCompanies: "getCompanies",
    }),
    companyOptions() {
      const list = (this.storeCompanies || []).map((company) => ({
        id: company.id,
        name: company.name,
      }));
      return [{ id: "all", name: "All Companies" }, ...list];
    },
    formattedRange() {
      const opts = { day: "numeric", month: "short", year: "numeric" };
      const from = this.dateFrom
        ? this.dateFrom.toLocaleDateString("en-GB", opts)
        : "";
      const to = this.dateTo
        ? this.dateTo.toLocaleDateString("en-GB", opts)
        : "";
      return `${from} - ${to}`;
    },
    disabledDatesFrom() {
      const end = this.startOfDay(this.dateTo);
      const today = this.startOfDay(new Date());
      return {
        customPredictor: (date) => {
          const day = this.startOfDay(date);
          if (day > today) return true;
          if (end && day > end) return true;
          return false;
        },
      };
    },
    disabledDatesTo() {
      const start = this.startOfDay(this.dateFrom);
      const today = this.startOfDay(new Date());
      return {
        customPredictor: (date) => {
          const day = this.startOfDay(date);
          if (day > today) return true;
          if (start && day < start) return true;
          return false;
        },
      };
    },
  },
  methods: {
    startOfDay(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    isBefore(dateA, dateB) {
      return this.startOfDay(dateA) < this.startOfDay(dateB);
    },
    onStartDateSelected(date) {
      if (!date) return;
      this.$emit("update:dateFrom", date);
      if (this.dateTo && this.isBefore(this.dateTo, date)) {
        this.$emit("update:dateTo", new Date(date));
      }
    },
    onEndDateSelected(date) {
      if (!date) return;
      if (this.dateFrom && this.isBefore(date, this.dateFrom)) {
        this.$emit("update:dateTo", new Date(this.dateFrom));
        return;
      }
      this.$emit("update:dateTo", date);
    },
  },
};
</script>

<style scoped lang="scss">
.ka-header {
  margin-bottom: 1.75rem;

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    flex-wrap: wrap;
  }

  &__intro {
    flex: 1 1 280px;
    min-width: 0;
  }

  &__title {
    font-size: 2rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.35rem;
    line-height: 1.2;
    letter-spacing: -0.02em;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    margin-left: 0.6rem;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    background: #fff7e6;
    border: 1px solid #fde3a7;
    color: #b7791f;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    cursor: help;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: #a8a8b3;
    margin: 0;
    line-height: 1.4;
  }

  &__controls {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.65rem;
    flex: 0 1 auto;
    justify-content: flex-end;
  }

  &__dates {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__date-field {
    position: relative;
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    padding: 0 0.75rem 0 0.65rem;
    min-height: 42px;
    min-width: 148px;

    &:focus-within {
      border-color: #c8c4f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
    }

    :deep(.vdp-datepicker) {
      width: 100%;
      min-width: 0;

      input {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0.55rem 0 !important;
        font-size: 0.875rem !important;
        color: #5e5873 !important;
        width: 100%;
        min-width: 0;
        cursor: pointer;
      }
    }
  }

  &__date-icon {
    width: 16px;
    height: 16px;
    color: #b9b9c3;
    flex-shrink: 0;
    margin-right: 0.4rem;
  }

  &__date-arrow {
    width: 16px;
    height: 16px;
    color: #c4c4cc;
    flex-shrink: 0;
  }

  &__select-wrap {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 42px;
  }

  &__select {
    appearance: none;
    background: #fff;
    border: 1px solid #e4e4e8;
    border-radius: 10px;
    min-height: 42px;
    padding: 0 2.1rem 0 0.9rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #5e5873;
    cursor: pointer;
    min-width: 150px;

    &:focus {
      outline: none;
      border-color: #c8c4f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.08);
    }
  }

  &__select-icon {
    position: absolute;
    right: 0.7rem;
    width: 14px;
    height: 14px;
    color: #b9b9c3;
    pointer-events: none;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    border: none;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 500;
    min-height: 42px;
    padding: 0 1.1rem;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }

  &__btn-icon {
    width: 16px;
    height: 16px;
    color: #fff;

    &--purple {
      color: #7367f0;
    }
  }

  &__btn--search {
    background: #7367f0;
    color: #fff;
    box-shadow: 0 4px 14px rgba(115, 103, 240, 0.28);

    &:hover:not(:disabled) {
      background: #685dd8;
    }
  }

  &__btn--download {
    background: #fff;
    color: #7367f0;
    border: 1px solid #e4e4e8;

    &:hover {
      border-color: #d0cce8;
      background: #faf9ff;
    }
  }
}

@media (max-width: 1100px) {
  .ka-header__controls {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 640px) {
  .ka-header__dates {
    width: 100%;
    flex-wrap: wrap;
  }

  .ka-header__date-field {
    flex: 1 1 130px;
  }

  .ka-header__btn {
    flex: 1 1 auto;
  }
}
</style>

<style lang="scss">
.ka-header .vdp-datepicker__calendar {
  border-radius: 10px;
  border: 1px solid #e4e4e8;
  box-shadow: 0 8px 24px rgba(34, 41, 47, 0.1);
}
</style>
